import React, { useState } from 'react';
import { CogIcon, TrashIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useFiles } from '../contexts/FilesContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';

const SettingsPage: React.FC = () => {
  const { clearAllFiles } = useFiles();
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState({
    autoProcess: true,
    saveToLocal: true,
    maxFileSize: 50,
    deleteAfterExport: false,
    theme: 'light'
  });

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    toast.success('Setting updated');
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all processed files? This action cannot be undone.')) {
      clearAllFiles();
      toast.success('All data cleared');
    }
  };

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ocr-settings.json';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Settings exported');
  };

  const SettingCard: React.FC<{
    title: string;
    description: string;
    children: React.ReactNode;
  }> = ({ title, description, children }) => (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{title}</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
        </div>
        <div className="ml-4">
          {children}
        </div>
      </div>
    </div>
  );

  const Toggle: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
  }> = ({ checked, onChange }) => (
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-brand-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="space-y-8">
      {/* Settings Header */}
      <div className="glass rounded-3xl shadow-soft p-8">
        <div className="flex items-center mb-8">
          <div className="p-3 bg-gradient-to-br from-gray-500 to-gray-700 rounded-2xl shadow-glow mr-4">
            <CogIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Application Settings</h2>
            <p className="text-gray-600 dark:text-gray-300">Configure your OCR processing preferences</p>
          </div>
        </div>

        {/* Processing Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Processing Options</h3>
          
          <SettingCard
            title="Auto-process files"
            description="Automatically start processing when files are uploaded"
          >
            <Toggle
              checked={settings.autoProcess}
              onChange={(checked) => handleSettingChange('autoProcess', checked)}
            />
          </SettingCard>

          <SettingCard
            title="Save to local storage"
            description="Keep processed files in browser storage for quick access"
          >
            <Toggle
              checked={settings.saveToLocal}
              onChange={(checked) => handleSettingChange('saveToLocal', checked)}
            />
          </SettingCard>

          <SettingCard
            title="Maximum file size"
            description="Set the maximum allowed file size for uploads (MB)"
          >
            <select
              value={settings.maxFileSize}
              onChange={(e) => handleSettingChange('maxFileSize', parseInt(e.target.value))}
              className="px-4 py-2 glass rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400"
            >
              <option value={5}>5 MB</option>
              <option value={10}>10 MB</option>
              <option value={20}>20 MB</option>
              <option value={50}>50 MB</option>
            </select>
          </SettingCard>

          <SettingCard
            title="Delete after export"
            description="Automatically delete processed files after successful export"
          >
            <Toggle
              checked={settings.deleteAfterExport}
              onChange={(checked) => handleSettingChange('deleteAfterExport', checked)}
            />
          </SettingCard>

          <SettingCard
            title="Theme"
            description="Choose your preferred color scheme"
          >
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as 'light' | 'dark')}
              className="px-4 py-2 glass rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </SettingCard>
        </div>
      </div>

      {/* Data Management */}
      <div className="glass rounded-3xl shadow-soft p-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Data Management</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass rounded-xl p-6 text-center">
            <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-xl inline-flex mb-4">
              <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Clear All Data</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Remove all processed files and reset the application
            </p>
            <button
              onClick={clearAllData}
              className="px-6 py-2 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors duration-200"
            >
              Clear Data
            </button>
          </div>

          <div className="glass rounded-xl p-6 text-center">
            <div className="p-3 bg-brand-100 dark:bg-brand-900/50 rounded-xl inline-flex mb-4">
              <DocumentArrowDownIcon className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Export Settings</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Download your current configuration as a backup
            </p>
            <button
              onClick={exportSettings}
              className="px-6 py-2 bg-brand-500 text-white rounded-xl font-semibold hover:bg-brand-600 transition-colors duration-200"
            >
              Export Settings
            </button>
          </div>
        </div>
      </div>

      {/* Application Info */}
      <div className="glass rounded-3xl shadow-soft p-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Application Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div>
            <p className="text-2xl font-bold text-brand-600 dark:text-brand-400 mb-1">v1.0.0</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Version</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">Active</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">API Status</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">React</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Framework</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
